import { ChatBot, NextPage, NextPageHead, PageView } from '@aspendental/shared-components-web';
import { PageThemeContextProvider } from '@aspendental/shared-components-web/lib/context/PageThemeProvider.ctx';
import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { getEntryById, updatePageContentsById } from './utils';
import getConfigByBrand from './utils/get-config-by-brand';
import { getDeviceTypeFromHeaders } from './utils/get-device-type-from-headers';
import getHostFromHeaders from './utils/get-host-from-headers';
import getThemeByDomainName from './utils/get-theme-by-domain-name';
import { Ref, RefData, templatesWithRTFs } from './utils/rich-text-entry-links';
import { CACHE_CONTROL_NO_CACHE, CACHE_CONTROL_SHORT_TTL } from '@src/constants';
import { AppMetadataByAppNameDocument, AppPagesDataByAppNameDocument, Entry, Page as IPage, LandingPageByAppNameWithPathDocument, type Page, PageByAppAndAlternatePathDocument, PageContentsItem, PageFragment } from '@src/lib/__generated/sdk';
import { getClient } from '@src/lib/client';
import { getLogger } from '@src/lib/logging/logger';
import choosePreview from '@src/pages/utils/choose-preview';
import convertPathToBreadCrumbTitle from '@src/pages/utils/convert-path-to-breadcrumb-title';
import DetermineIndexing from '@src/pages/utils/determine-indexing';
import getConfigServices from '@src/pages/utils/get-config-services';
import { getServerSideTranslations } from '@src/pages/utils/get-serverside-translations';
const Page = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const pageData = useContentfulLiveUpdates(props.page);
  const {
    url: logoUrl
  } = props.appData?.logo ?? {};
  const {
    path,
    title,
    indexed,
    description,
    canonical,
    disableChatBot = false,
    analyticsContext,
    ogImage
  } = pageData;
  const pageTheme = props.page?.pageTheme || 'Primary';
  const {
    siteURL,
    features,
    featureFlags,
    logo
  } = props.config as BrandThemeConfig;
  const {
    preview,
    breadcrumbsData,
    pageReferrer,
    domain
  } = props;
  return <PageThemeContextProvider pageTheme={pageTheme} data-sentry-element="PageThemeContextProvider" data-sentry-component="Page" data-sentry-source-file="[...slug].page.tsx">
			<PageView analyticsContext={analyticsContext} pageReferrer={pageReferrer} siteSection="Marketing" data-sentry-element="PageView" data-sentry-source-file="[...slug].page.tsx" />
			<NextPageHead path={path} title={title} indexed={DetermineIndexing(indexed, props.env, preview, featureFlags.doNotIndexWholeSite, domain)} description={description} canonical={canonical} breadcrumbTitle={breadcrumbsData?.breadcrumbTitle} breadcrumbs={breadcrumbsData?.breadcrumbsCollection.items} originUrl={siteURL} ogImage={ogImage?.desktopImage?.image?.cdn_url ?? logo.dark} data-sentry-element="NextPageHead" data-sentry-source-file="[...slug].page.tsx" />
			<NextPage pageData={{
      ...pageData
    }} data-sentry-element="NextPage" data-sentry-source-file="[...slug].page.tsx" />
			{!disableChatBot && <ChatBot url={features.chat.chatBotURL} isIconButton={features.chat.isIconButton} logoUrl={logoUrl} featureList={featureFlags.featureList} />}
		</PageThemeContextProvider>;
};
export const getServerSideProps: GetServerSideProps = async ({
  params,
  locale,
  req,
  res,
  preview = false
}) => {
  if (!params?.slug) {
    return {
      notFound: true
    };
  }
  const domain = getHostFromHeaders(req?.headers);
  const theme = getThemeByDomainName(domain);
  const headerTagTheme = Array.isArray(req.headers['x-tag-theme']) ? (req.headers['x-tag-theme'] as TagThemeType[])[0] : req.headers['x-tag-theme'] as TagThemeType;
  // Priority: HTTP header x-tag-theme > domain > process.env.TAG_BRAND_THEME
  const tagTheme = headerTagTheme || theme || process.env.TAG_BRAND_THEME || 'aspendental';
  const logger = getLogger('landing-page', tagTheme);
  const deviceType = getDeviceTypeFromHeaders(req.headers);
  const pageReferrer = req.headers.referer || '';
  const stateCodeFromHeaders = req.headers['cloudfront-viewer-country-region'] || '';
  try {
    const brandConfig = getConfigByBrand(tagTheme);
    preview = choosePreview(domain, preview);
    const appName = brandConfig.contentfulAppId || process.env.TAG_APP_NAME || 'TAG_Commercial_Web_Sandbox';
    const gqlClient = getClient(preview);
    if (gqlClient === null) {
      logger.error('GQL_CLIENT_IS_NULL: missing token for fetching contentful data');
      throw new Error('gqlClient is null');
    }
    const [appPagesData, appMetadata] = await Promise.all([gqlClient.query(AppPagesDataByAppNameDocument, {
      appName,
      preview
    }).toPromise(), gqlClient.query(AppMetadataByAppNameDocument, {
      appName,
      preview
    }).toPromise()]);
    const appData = {
      ...appPagesData.data?.appCollection?.items[0],
      ...appMetadata.data?.appCollection?.items[0]
    };

    // replace [env] with environment name for locationBarFindOfficeUrl
    // TODO: create a brand variable context to replace those variables from CMS in future
    if (appData && appData.header?.locationBarFindOfficeUrl) {
      const brandSubdomain = process.env.ENVIRONMENT_NAME !== 'prod' ? 'wwwstg' : 'www';
      appData.header.locationBarFindOfficeUrl = appData.header?.locationBarFindOfficeUrl?.replace('[env]', brandSubdomain);
    }
    if (!appData) {
      return {
        notFound: true
      };
    }
    const slugs = params.slug as string[];
    const path = '/' + slugs.join('/');
    const alternativePath = path.endsWith('/') ? path : path + '/';
    const pageData = await gqlClient.query(PageByAppAndAlternatePathDocument, {
      appName,
      path,
      alternativePath,
      preview
    }).toPromise();
    if (pageData.error) {
      logger.error(`CONTENTFUL_ERROR_SLUG_PAGE: PATH - ${path} - ERROR: ${pageData.error}`);
    }
    const page = pageData.data.pageCollection?.items[0] as PageFragment;
    if (!page) {
      logger.error(`COULD_NOT_FIND_THE_PAGE_WITH_PATH: ${path}`);
      return {
        notFound: true
      };
    }
    const getEntryLinks = async (item: PageContentsItem) => {
      if (!item?.__typename) return;
      const templateNames = Object.keys(templatesWithRTFs);
      const hasRichTextField = templateNames.includes(item.__typename);
      if (!hasRichTextField) return;
      const contentType = item.__typename;
      try {
        const templateLinks = gqlClient.query(templatesWithRTFs[contentType].query, {
          id: item?.sys?.id,
          preview
        }).toPromise();
        if (!templateLinks) {
          return undefined;
        }
        return templateLinks;
      } catch (e) {
        logger.error(`[${contentType}] ENTRY_LINKS_FETCH_FAILURE: ${e}`);
      }
    };

    // Define the maximum depth for resolving nested entry links in Rich Text Fields.
    // Depth 0: Initial items in pageContents.
    // Depth 5: The deepest level of entries whose Rich Text Fields will be resolved.
    const MAX_SUPPORTED_ENTRY_LINK_DEPTH = 5;
    const addEntryLinksToRichTextFields = (pageContents, templateLinks) => {
      return pageContents.map((item: PageContentsItem) => {
        if (!item || !item.__typename) return item;
        const {
          __typename: contentType
        } = item;
        const contentTypeId = contentType.charAt(0).toLowerCase() + contentType.slice(1);
        const {
          fields: richTextFields,
          refs: templateRefsWithRtf
        } = templatesWithRTFs[contentType] || {};
        const findRichTextLinksFragment = () => templateLinks.find(links => {
          return links?.data[contentTypeId]?.sys?.id === item?.sys?.id;
        });
        const richTextLinksFragment = findRichTextLinksFragment();
        if (!richTextLinksFragment) return item;

        // Helper function to recursively process nested references
        const processNestedReferencesRecursively = (definingRefData: RefData, parentLinksObject: Entry, parentEnrichedItem: any, parentItemDepth: number) => {
          if (!definingRefData.refs || !parentLinksObject || !parentEnrichedItem) {
            return;
          }
          definingRefData.refs.forEach((currentRefFieldConfig: Ref) => {
            // Config for a reference field on parentLinksObject (e.g., 'blocksCollection' field)
            const refFieldName = currentRefFieldConfig.name; // Name of the reference field (e.g., "blocksCollection")

            // Data from the LINKS FRAGMENT for the current reference field
            const linksSourceForCurrentRefField = parentLinksObject[refFieldName];
            // Data from the MAIN PAGE (enriched content) for the current reference field
            const enrichedSourceForCurrentRefField = parentEnrichedItem[refFieldName];
            if (!linksSourceForCurrentRefField || !enrichedSourceForCurrentRefField) {
              return;
            }

            // Normalize to arrays for uniform processing (handles both single and collection references)
            const actualLinksEntries: Entry[] = isCollection(refFieldName) ? linksSourceForCurrentRefField.items || [] : [linksSourceForCurrentRefField];
            const actualEnrichedEntries: Entry[] = isCollection(refFieldName) ? enrichedSourceForCurrentRefField.items || [] : [enrichedSourceForCurrentRefField];
            actualLinksEntries.forEach((currentLinkEntry: Entry) => {
              // An entry from the links fragment (e.g. a specific block)
              if (!currentLinkEntry?.sys?.id) return;

              // Find the corresponding entry in the main page data structure
              const currentEnrichedEntry = actualEnrichedEntries.find((enrichedInstance: Entry) => enrichedInstance?.sys?.id === currentLinkEntry?.sys?.id);
              if (!currentEnrichedEntry) return;

              // For this currentLinkEntry & currentEnrichedEntry pair, iterate over RefData array which defines RTFs on this type of entry
              currentRefFieldConfig.data.forEach((rtfRefData: RefData) => {
                // rtfRefData is the config for an RTF on currentLinkEntry
                const rtfFieldName = rtfRefData.richTextField;

                // 1. Enrich the RTF on currentEnrichedEntry
                const linksForRtf = currentLinkEntry[rtfFieldName]?.links;
                if (linksForRtf && currentEnrichedEntry[rtfFieldName]) {
                  currentEnrichedEntry[rtfFieldName].links = linksForRtf;
                }

                // Current child item's depth is parentItemDepth + 1
                const childItemDepth = parentItemDepth + 1;

                // 2. If this rtfRefData itself has 'refs', recurse for the next level
                // Only recurse if the current child's depth is less than the max allowed depth.
                // This means children up to MAX_SUPPORTED_ENTRY_LINK_DEPTH will be processed,
                // but we won't look for *their* children if they are already at MAX_SUPPORTED_ENTRY_LINK_DEPTH.
                if (hasRefs(rtfRefData) && childItemDepth < MAX_SUPPORTED_ENTRY_LINK_DEPTH) {
                  processNestedReferencesRecursively(rtfRefData, currentLinkEntry, currentEnrichedEntry, childItemDepth);
                }
              });
            });
          });
        };
        if (richTextFields) {
          richTextFields.forEach((field: string) => {
            const richTextLinks = richTextLinksFragment.data[contentTypeId][field]?.links;
            if (richTextLinks && item[field]) item[field].links = richTextLinks;
          });
        }
        const isCollection = (refName: string) => {
          return refName.includes('Collection');
        };
        const hasRefs = (refData: RefData): boolean => {
          return 'refs' in refData;
        };
        if (templateRefsWithRtf) {
          templateRefsWithRtf.forEach(ref => {
            const linksData = isCollection(ref.name) ? richTextLinksFragment.data[contentTypeId][ref.name].items : richTextLinksFragment.data[contentTypeId][ref.name];
            if (!linksData) return;

            // Process L1 references (references directly on the 'item')
            if (isCollection(ref.name)) {
              // If the L1 reference is a collection
              if (!Array.isArray(linksData)) return; // linksData are L1 items from links fragment
              linksData.forEach((linksObject_L1: Entry) => {
                // For each L1 item from links fragment
                const objectId_L1 = linksObject_L1?.sys?.id;
                if (!objectId_L1) return;

                // Find corresponding L1 item in the main page data
                const enrichedItem_L1 = item[ref.name]?.items?.find((instance: Entry) => instance?.sys?.id === objectId_L1);
                if (!enrichedItem_L1) return;
                ref.data.forEach((refData_on_L1: RefData) => {
                  // For each RTF config on this L1 item type
                  // Enrich RTF on the L1 item itself
                  const links = linksObject_L1[refData_on_L1.richTextField]?.links;
                  if (links && enrichedItem_L1[refData_on_L1.richTextField]) {
                    enrichedItem_L1[refData_on_L1.richTextField].links = links;
                  }
                  // enrichedItem_L1 is at depth 1. Its children would be at depth 2.
                  // The parentItemDepth for this call is 1 (depth of enrichedItem_L1).
                  // If this L1 RTF config also defines further references, recurse
                  // We recurse if depth 1 (parentItemDepth) + 1 < MAX_SUPPORTED_ENTRY_LINK_DEPTH
                  // This means the children (at depth 2) are within limits.
                  const L1_ITEM_DEPTH = 1;
                  if (hasRefs(refData_on_L1) && L1_ITEM_DEPTH + 1 < MAX_SUPPORTED_ENTRY_LINK_DEPTH) {
                    processNestedReferencesRecursively(refData_on_L1, linksObject_L1, enrichedItem_L1, L1_ITEM_DEPTH);
                  }
                });
              });
            } else {
              // If the L1 reference is a single item
              const linksObject_L1_single = linksData as Entry; // L1 item from links fragment
              const enrichedItem_L1 = item[ref.name]; // Corresponding L1 item in main page data

              if (!linksObject_L1_single || !enrichedItem_L1) return;
              ref.data.forEach((refData_on_L1: RefData) => {
                // For each RTF config on this L1 item type
                const links = linksObject_L1_single[refData_on_L1.richTextField]?.links;
                if (links && enrichedItem_L1[refData_on_L1.richTextField]) {
                  enrichedItem_L1[refData_on_L1.richTextField].links = links;
                }
                // enrichedItem_L1 is at depth 1.
                // The parentItemDepth for this call is 1.
                const L1_ITEM_DEPTH = 1;
                // If this L1 RTF config also defines further references, recurse
                if (hasRefs(refData_on_L1) && L1_ITEM_DEPTH + 1 < MAX_SUPPORTED_ENTRY_LINK_DEPTH) {
                  processNestedReferencesRecursively(refData_on_L1, linksObject_L1_single, enrichedItem_L1, L1_ITEM_DEPTH);
                }
              });
            }
          });
        }
        return item;
      });
    };

    // fetch by id of content in contents
    const contentByIdPromises: Promise<any>[] = [];
    const initialPageContentsPromises = page?.contentsCollection?.items?.map(async item => {
      const contentByIdPromise = getEntryById(gqlClient, preview, logger, item as PageContentsItem);
      contentByIdPromises.push(contentByIdPromise);
      return item;
    });
    const initialPageContents = initialPageContentsPromises?.length ? (await Promise.all(initialPageContentsPromises)).filter(Boolean) : [];
    const contentsFetchedById = (await Promise.all(contentByIdPromises)).filter(Boolean);

    // update contents data by id
    const enrichedContentsById = updatePageContentsById(initialPageContents as PageContentsItem[], contentsFetchedById);

    // Add links information for rich text rendering
    const entryLinksPromises: Promise<any>[] = [];
    const pageContentsPromises = enrichedContentsById.map(async item => {
      const entryLinksPromise = getEntryLinks(item as PageContentsItem);
      entryLinksPromises.push(entryLinksPromise);
      return item;
    });
    const pageContents = (await Promise.all(pageContentsPromises)).filter(Boolean);
    const entryLinks = (await Promise.all(entryLinksPromises)).filter(Boolean);

    // final contents to display on page
    const enrichedPageContents = addEntryLinksToRichTextFields(pageContents, entryLinks);

    // breadcrumb data generation from url path if not in page data
    const breadcrumbsData: {
      path: string | null | undefined;
      breadcrumbTitle: string | null | undefined;
      breadcrumbsCollection: null | {
        items: any[];
      };
    } = {
      path: page.path,
      breadcrumbTitle: page.breadcrumbTitle,
      breadcrumbsCollection: null
    };
    const pathListWithoutCurrentPage = slugs.slice(0, slugs.length - 1);
    const breadcrumbItemsPromises: Promise<any>[] = [];
    const getPageDetails = async (path: string) => {
      try {
        const pageDataForThePath = await gqlClient.query(LandingPageByAppNameWithPathDocument, {
          appName,
          path,
          preview
        }).toPromise();
        const pageForThePath = pageDataForThePath.data.pageCollection?.items[0] as PageFragment;
        const breadcrumbTitle = pageForThePath?.breadcrumbTitle ? pageForThePath?.breadcrumbTitle : convertPathToBreadCrumbTitle(path);
        return {
          breadcrumbTitle,
          path: pageForThePath?.path || '/'
        } as IPage;
      } catch (e) {
        logger.error(`ERROR_WHILE_FETCHING_PAGE_DATA_FOR_BREADCRUMBS: ${e}`);
        return null;
      }
    };
    let pathToCheck = '';
    for (const path of pathListWithoutCurrentPage) {
      const pathToAppendWithTrailingSlash = pathToCheck === '' ? `/${path}/` : `${path}/`;
      pathToCheck += pathToAppendWithTrailingSlash;
      const pageDetails = await getPageDetails(pathToCheck);
      if (pageDetails) {
        breadcrumbItemsPromises.push(Promise.resolve(pageDetails));
      }
    }
    const breadcrumbsItems = (await Promise.all(breadcrumbItemsPromises)).filter(Boolean);
    const currentPagePath = slugs[slugs.length - 1];
    if (!breadcrumbsData.breadcrumbTitle) {
      breadcrumbsData.breadcrumbTitle = convertPathToBreadCrumbTitle(currentPagePath);
    }
    breadcrumbsItems.unshift({
      breadcrumbTitle: 'Home',
      path: '/'
    } as IPage);

    // Assign generated breadcrumbs to `breadcrumbsData`
    breadcrumbsData.breadcrumbsCollection = {
      items: breadcrumbsItems
    };
    if (preview) {
      res.setHeader('Cache-Control', CACHE_CONTROL_NO_CACHE);
    } else {
      res.setHeader('Cache-Control', CACHE_CONTROL_SHORT_TTL);
    }
    let hasIpBasedCoordinates = false;
    let coordinates = {
      latitude: 0,
      longitude: 0
    };
    if (req.headers['cloudfront-viewer-latitude'] && req.headers['cloudfront-viewer-longitude']) {
      hasIpBasedCoordinates = true;
      coordinates = {
        latitude: parseFloat(req.headers['cloudfront-viewer-latitude'] as string),
        longitude: parseFloat(req.headers['cloudfront-viewer-longitude'] as string)
      };
      logger.info(`CDN_GEO_LOCATION_FOUND: [${coordinates?.latitude}, ${coordinates?.longitude}]`);
    } else {
      logger.info(`CDN_GEO_LOCATION_MISS`);
    }
    const transformedServices = getConfigServices(brandConfig);
    return {
      props: {
        ...(await getServerSideTranslations(locale)),
        env: {
          ENVIRONMENT_NAME: process.env.ENVIRONMENT_NAME,
          CC_DENTRINO_CLIENT_ID: process.env.CC_DENTRINO_CLIENT_ID ?? '',
          CC_DENTRINO_CLIENT_SECRET: process.env.CC_DENTRINO_CLIENT_SECRET ?? '',
          DENTRINO_CLIENT_ID: process.env.DENTRINO_CLIENT_ID,
          DENTRINO_CLIENT_SECRET: process.env.DENTRINO_CLIENT_SECRET ?? '',
          ZENOTI_API_KEY_LIV: process.env.ZENOTI_API_KEY_LIV,
          ZENOTI_CENTER_ID: process.env.ZENOTI_CENTER_ID,
          ZENOTI_CENTER_ID_IL: process.env.ZENOTI_CENTER_ID_IL
        },
        config: {
          ...brandConfig,
          ...(appData.featureFlags ? {
            featureFlags: appData.featureFlags
          } : {}),
          services: transformedServices,
          schemaUrl: `${brandConfig.siteURL}${req.url}`
        },
        page: {
          ...page,
          contentsCollection: {
            items: enrichedPageContents
          }
        },
        user: {
          coordinates,
          hasIpBasedCoordinates
        },
        appData,
        footerData: appData?.footer,
        preview,
        breadcrumbsData,
        deviceType,
        pageReferrer,
        stateCodeFromHeaders,
        domain: domain ?? ''
      }
    };
  } catch (e) {
    logger.error(`LANDING_PAGE_LOAD_FAILURE: ${e}`);
    return {
      notFound: true
    };
  }
};
export default Page;